import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './RequestsContainer.module.scss';
import { DataVendor, SearchEntity, FetchSearchesParams } from 'v2/models';
import { useInfiniteQuery } from 'react-query';
import { PAGE_SIZE } from 'utils/constants';
import RequestsList from './RequestsList';
import { Spinner } from 'react-bootstrap';
import { fetchSearches } from 'v2/hooks/useFetchSearchesPaginated';
import RequestDetail from './RequestDetail';

export const buildRequestsParams = (dataVendor: DataVendor) => {
  return {
    depth: 'master-organization',
    data_provider: dataVendor.data_provider,
    data_provider_response: 'affirmative',
  };
};

type RequestsProps = {
  dataVendor: DataVendor;
};

const RequestsContainer = ({ dataVendor }: RequestsProps) => {
  const [requestSelected, setRequestSelected] = useState<SearchEntity | null>(
    null
  );

  const { data, isFetching, fetchNextPage, hasNextPage, status } =
    useInfiniteQuery(
      [
        'get all requests inside the master organization related to the data provider',
      ],
      async ({ pageParam = 1 }) => {
        const data = await fetchSearches({
          ...buildRequestsParams(dataVendor),
          page: pageParam,
          size: PAGE_SIZE,
        } as FetchSearchesParams);

        return {
          ...data,
          prev: pageParam,
          next: pageParam + 1,
        };
      },
      {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage) => {
          const hasMorePages = PAGE_SIZE * lastPage.prev < lastPage.count;
          return hasMorePages ? lastPage.next : undefined;
        },
        onSuccess: (data) => {
          if (requestSelected === null && data.pages[0].count) {
            setRequestSelected(data.pages[0].results[0]);
          }
        },
      }
    );

  const [initialFetchComplete, setInitialFetchComplete] = useState(false);

  useEffect(() => {
    if (status === 'success' && !initialFetchComplete) {
      setInitialFetchComplete(true);
    }
  }, [status, initialFetchComplete]);

  if (
    dataVendor.data_provider === undefined ||
    dataVendor.data_provider === null
  ) {
    return null;
  }

  if (!initialFetchComplete) {
    return (
      <div className="loadingIndicatorContainer">
        <Spinner role="progressbar" animation="border" />
      </div>
    );
  }

  return (
    <div
      aria-label="requests-container"
      className={classnames('container-xl', styles.requestsContainer)}
    >
      <div className={classnames(styles.leftContent)}>
        {data !== undefined && (
          <RequestsList
            data={data}
            infiniteQueryFetch={{ isFetching, hasNextPage, fetchNextPage }}
            selected={requestSelected}
            onSelect={setRequestSelected}
          />
        )}
      </div>

      {requestSelected && (
        <RequestDetail request={requestSelected} dataVendor={dataVendor} />
      )}
    </div>
  );
};

export default RequestsContainer;
